import React, { useMemo, useEffect, useCallback, useContext } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { PixelFactoryContext } from 'pixel-base'
import { useApi } from 'shared/hooks'
import { get } from 'utils/api-routes.util'
import RouteGuard from './components/RouteGuard'
import { ROUTES } from './pages/routes'
import { IPixelFactory } from '../../decl.d'

interface IResponse {
  data: ISession
}

interface ISession {
  billing_status: string
  billing_status_message: string
}

const checkAuth = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('user_info') || '{}')
    return Boolean(userData?.token)
  } catch {
    return false
  }
}

const checkOtpVerification = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('user_info') || '{}')
    return Boolean(userData?.is_otp_verified)
  } catch {
    return false
  }
}

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(checkAuth)

  const updateAuth = useCallback(() => {
    setIsAuthenticated(checkAuth())
  }, [])

  useEffect(() => {
    updateAuth()

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user_info') {
        updateAuth()
      }
    }

    const handleAuthChange = () => {
      updateAuth()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('auth-change', handleAuthChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth-change', handleAuthChange)
    }
  }, [updateAuth])

  return isAuthenticated
}

const useOtpVerification = () => {
  const [isOtpVerified, setIsOtpVerified] = React.useState(checkOtpVerification)

  const updateOtpVerification = useCallback(() => {
    setIsOtpVerified(checkOtpVerification())
  }, [])

  useEffect(() => {
    updateOtpVerification()

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user_info') {
        updateOtpVerification()
      }
    }

    const handleAuthChange = () => {
      updateOtpVerification()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('auth-change', handleAuthChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth-change', handleAuthChange)
    }
  }, [updateOtpVerification])

  return isOtpVerified
}

const Routing = () => {
  // Safe context access with null check
  const pixelFactoryContext = useContext<IPixelFactory>(PixelFactoryContext)
  const showPixelBanner = pixelFactoryContext?.showPixelBanner

  const isAuthenticated = useAuth()
  const isOtpVerified = useOtpVerification()
  const location = useLocation()
  const [sessionChecked, setSessionChecked] = React.useState(false)

  useApi<IResponse>(() => get('/auth/session'), {
    onSuccess: (res) => {
      if (res.data) {
        if (res.data.billing_status === 'overdue') {
          showPixelBanner?.({
            label: res.data.billing_status_message,
            dismissAfter: 0,
            type: 'error',
            isDismissible: true,
            icon: faCircleInfo,
            isShowBanner: true,
          })
        }
      }
      setSessionChecked(true)
    },
    onError: () => {
      setSessionChecked(true)
    },
    skip: !isAuthenticated || sessionChecked,
  })

  const routes = useMemo(() => {
    const filteredRoutes = ROUTES.filter((route) => {
      if (!isAuthenticated) {
        return !route.isProtected
      }

      // If authenticated, include protected routes and auth routes for OTP verification
      if (route.isProtected) {
        return true
      }

      // Include signup route when OTP is not verified (for email verification)
      if (route.path === '/auth/signup' && !isOtpVerified) {
        return true
      }

      // Include OTP verification route when OTP is not verified
      if (route.path === '/auth/signup-otp' && !isOtpVerified) {
        return true
      }

      // Exclude other auth routes when authenticated
      return !route.path.startsWith('/auth/')
    })

    return filteredRoutes.map(
      (
        { path, component: Component, isProtected, redirectTo, guard },
        index,
      ) => {
        // Determine if OTP verification is required for this route
        const requireOtpVerification =
          isProtected && path !== '/auth/signup-otp'

        return (
          <Route
            key={`${path}-${index}`}
            path={path}
            element={
              <RouteGuard
                isProtected={isProtected}
                redirectTo={
                  redirectTo || (isProtected ? '/auth/login' : '/orders')
                }
                guard={guard}
                isAuthenticated={isAuthenticated}
                requireOtpVerification={requireOtpVerification}
                isOtpVerified={isOtpVerified}
              >
                <Component />
              </RouteGuard>
            }
          />
        )
      },
    )
  }, [isAuthenticated, isOtpVerified, location.pathname])

  const defaultRedirect = useMemo(() => {
    if (!isAuthenticated) {
      return <Navigate to="/auth/login" replace />
    }

    // If authenticated but OTP not verified, redirect to OTP page
    if (!isOtpVerified) {
      return <Navigate to="/auth/signup-otp" replace />
    }

    // If authenticated and OTP verified, redirect to orders
    return <Navigate to="/orders" replace />
  }, [isAuthenticated, isOtpVerified])

  return (
    <Routes>
      {routes}
      <Route path="*" element={defaultRedirect} />
    </Routes>
  )
}

Routing.displayName = 'Routing'

export default Routing
